import { BaseControlDef, BaseElementDefBase } from "./BaseElementDef";
import { data as standardDropdownLists, mapping as standardDropdownListsMapping, counties as standardDropdownListsMappingIdCounties } from "../../lib/standardDropDownListItems";
import { FormDefinitionItem } from "./__DefinitionDataInterface";
import { DropDownListSettings } from "@cs-ts/drop-down-list-settings";
import { SelectListItem } from "@cs/SelectListItem";
import { SelectListGroup } from "@cs/SelectListGroup";
import { ListItem } from "@cs/ListItem";
import { CalculationContext } from "./__CalculationContextInterface";
import { from, combineLatest, isObservable } from "rxjs";
import { Observable } from "rxjs"; // eslint-disable-line @typescript-eslint/no-unused-vars
import { map } from "rxjs/operators";
import { throwIfNonSuccessStatus } from "@/lib/fetchHelpers.3";
import { isNullOrWhitespace } from "@/lib/whitespaceMethods";
import { tryParseDate, DfStrictParseFloat } from "../../lib/calculations.shared";
import { FormControlItem } from "@/cs-ts-bridge/cs/FormControlItem";
import FormDef from "./FormDef";

const resolveDatasourceItemsCache = {} as Record<string, Promise<{ effectiveValue: string; text: string; group: SelectListGroup }[]>>;

export default class DropDownListDef extends BaseControlDef<DropDownListSettings, FormDefinitionItem<DropDownListSettings>, FormControlItem, string> {
    declare definition: FormDefinitionItem<DropDownListSettings>;

    constructor(parentElement: BaseElementDefBase | null, elementData: FormControlItem, definitionData: FormDefinitionItem<DropDownListSettings>, calculationContext: CalculationContext) {
        super(parentElement, elementData, definitionData, calculationContext);
        this.typeName = "MI.Common.DynamicForms.Models.DFDropDownList"; // TODO - not hardcoded?
        this.isFocusable = true;
    }

    resolveItems(groups: string[], texts: string[], values: string[]) {
        groups = groups || [null];
        values = values || texts;
        const items = [];
        const length = Math.max(
            ...[groups, texts, values].map(function (v) {
                return v.length;
            }),
        );
        for (let i = 0; i < length; i++) {
            const text = texts.length <= i ? texts[texts.length - 1] : texts[i];
            const value = values.length <= i ? values[values.length - 1] : values[i];
            const group = groups.length <= i ? groups[groups.length - 1] : groups[i];
            items.push({
                text: text,
                effectiveValue: value == null || value == "" ? text : value,
                group: group == null || group == "" ? null : { name: group },
            });
        }
        return items;
    }

    async itemDatasourceAsync() {
        if (this.definition.settings.datasourceTable == null) {
            return [];
        }
        const target = this.id;
        const targetPathArray = this.getElementPathIndexFromRoot();
        const cacheKey = JSON.stringify([target, targetPathArray]);
        if (!(cacheKey in resolveDatasourceItemsCache)) {
            resolveDatasourceItemsCache[cacheKey] = (async () => {
                const formNode = this.getRoot() as FormDef;
                const url = await this.calculationContext.urlFetcher("invokeDraft", { formId: formNode.formId! });
                const data = {
                    formName: formNode.formName,
                    formVersion: formNode.formVersion,
                    formHash: formNode.formHash,
                    segment: null,
                    skipForm: true,
                    noWrite: true,
                    asAdmin: this.calculationContext.adminReview || false, // TODO
                    inPreview: this.calculationContext.adminPreview || false, // TODO
                    testMode: this.calculationContext.testMode || false, // TODO
                    target,
                    targetPathArray,
                    type: "resolveDatasourceItems", // TODO magic string
                };

                // TODO - this fails for preview since it fetches the last published
                try {
                    const resp = await this.calculationContext.fetchXsrf(url, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(data),
                    });
                    const outerResult = await resp.json();
                    const result = JSON.parse(outerResult.Result) as SelectListItem[];
                    return result.map(v => ({
                        text: v.Text!,
                        group: v.Group!,
                        effectiveValue: v.Value!,
                    }));
                } catch (e) {
                    console.log(e);
                    return [];
                    //if (breakpoint(context)) {
                    //    debugger;
                    //    throw e;
                    //}
                }
            })();
        }
        return await resolveDatasourceItemsCache[cacheKey];
    }

    itemStock() {
        const type = this.definition.settings.standardDropdownList;
        if (type == standardDropdownListsMappingIdCounties) {
            const urlFetcher = this.calculationContext.urlFetcher;
            return (async () => {
                const url = await urlFetcher("standardDropDownListItems", { type: type.toString() });
                const resp = await fetch(url);
                throwIfNonSuccessStatus(resp);
                const data = (await resp.json()) as ListItem[];
                return data.map(v => ({
                    text: v.Text,
                    effectiveValue: v.Value,
                }));
            })();
        } else if (type != undefined) {
            return standardDropdownLists[standardDropdownListsMapping[type]].map(v => ({
                text: v.text,
                effectiveValue: v.value,
            }));
        } else {
            throw new Error();
        }
    }

    items$() {
        let result = null;
        if (this.definition.settings.listDataType == 0 || (this.definition.settings.listDataType == null && this.definition.settings.listItems != null && this.definition.settings.listItems.length > 0)) {
            // TODO constant
            // TODO old list items?
            const items = this.definition.settings.listItems ?? [];
            const item$s = [];
            for (let index = 0; index < items.length; index++) {
                const item = items[index];
                const group$ = item.group ? from([[item.group]]) : this.resolveCalculation(item.groupCalculationId!, item.legacyGroupCalculation);
                const text$ = item.text ? from([[item.text]]) : this.resolveCalculation(item.textCalculationId!, item.legacyTextCalculation);
                const effectiveValue$ = item.effectiveValue ? from([[item.effectiveValue]]) : this.resolveCalculation(item.valueCalculationId!, item.legacyValueCalculation);
                //@ts-ignore
                item$s.push(combineLatest([text$, effectiveValue$, group$]).pipe(map(([text, effectiveValue, group]) => (text.length >= effectiveValue.length ? text.map((i, j) => ({ text: i, effectiveValue: effectiveValue[j], group: group[j] })) : effectiveValue.map((i, j) => ({ effectiveValue: i, text: text[j], group: group[j] }))).map(({ text, effectiveValue, group }) => ({ text, group: group == null || group == "" ? null : { name: group }, effectiveValue: effectiveValue == null || /^\s*$/.test(effectiveValue) ? text : effectiveValue })))));
            }
            result = combineLatest(item$s).pipe(map(i => i.flat()));
        } else if (this.definition.settings.listDataType == 2) {
            // TODO constant
            result = this.itemDatasourceAsync();
        } else if (this.definition.settings.listDataType == 1 || (this.definition.settings.listDataType == null && this.definition.settings.standardDropdownList != null)) {
            result = this.itemStock();
        }
        return (isObservable(result) ? result : from(result != null && "then" in result ? result : [result])) as Observable<{ group?: { name: string }; text: string; effectiveValue: string | undefined }[]>;
    }

    sortedItems$() {
        let items$ = this.items$();
        if (this.definition.settings.sortList !== false) {
            items$ = items$.pipe(
                map(items => {
                    items.sort(function (a, b) {
                        const aGroupName = a.group == null ? null : a.group.name;
                        const bGroupName = b.group == null ? null : b.group.name;
                        if (aGroupName == bGroupName) {
                            const aNumber = DfStrictParseFloat(a.text);
                            const bNumber = DfStrictParseFloat(b.text);
                            if (!isNaN(aNumber) && !isNaN(bNumber)) {
                                return aNumber > bNumber ? 1 : aNumber < bNumber ? -1 : 0;
                            } else if (!isNaN(aNumber)) {
                                return -1;
                            } else if (!isNaN(bNumber)) {
                                return 1;
                            }
                            const aDate = tryParseDate(a.text);
                            const bDate = tryParseDate(b.text);
                            if (aDate != null && bDate != null) {
                                return aDate > bDate ? 1 : aDate < bDate ? -1 : 0;
                            } else if (aDate != null) {
                                return -1;
                            } else if (bDate != null) {
                                return 1;
                            }
                            return a.text > b.text ? 1 : a.text < b.text ? -1 : 0;
                        }
                        return aGroupName! > bGroupName! ? 1 : -1;
                    });
                    function itemEquals(a: (typeof items)[0], b: (typeof items)[0]) {
                        return (a.group == null ? b.group == null : b.group != null && a.group.name == b.group.name) && (a.text == null ? b.text == null : a.text == b.text) && (a.effectiveValue == null ? b.effectiveValue == null : a.effectiveValue == b.effectiveValue);
                    }
                    const newItems = [] as typeof items;
                    items.forEach(c => {
                        if (!newItems.length || !itemEquals(newItems[newItems.length - 1], c)) {
                            newItems.push(c);
                        }
                    });
                    return newItems;
                }),
            );
        }
        return items$;
    }

    groupedItems$() {
        return this.sortedItems$().pipe(
            map(sortedItems => {
                const getCaseInsensitiveNameProperty = (obj: object) => Object.keys(obj).find(key => key.toLocaleLowerCase() == "name")!;
                const items = sortedItems.filter(function (v) {
                    return v.group != null && v.group[getCaseInsensitiveNameProperty(v.group) as "name"] != "";
                });
                const result = [] as { key: string; items: typeof items }[];
                const groups = {} as Record<string, (typeof result)[0]>;
                items.forEach(function (v) {
                    let group = groups[v.group![getCaseInsensitiveNameProperty(v.group!) as "name"]];
                    if (group == null) {
                        group = {
                            key: v.group![getCaseInsensitiveNameProperty(v.group!) as "name"],
                            items: [v],
                        };
                        groups[group.key] = group;
                        result.push(group);
                    } else {
                        group.items.push(v);
                    }
                });
                return result;
            }),
        );
    }

    ungroupedItems$() {
        return this.sortedItems$().pipe(
            map(sortedItems => {
                const items = sortedItems.filter(function (v) {
                    return v.group == null || v.group.name == "";
                });
                let placeholderText = this.definition.settings.placeholderText;
                if (isNullOrWhitespace(placeholderText)) {
                    placeholderText = "-- Select an option --";
                }
                items.unshift({ effectiveValue: undefined, text: placeholderText! });
                return items;
            }),
        );
    }
}
